/**
 * useStripeCallback — gère le retour de Stripe Checkout sur web.
 *
 * Quand l'utilisateur termine un paiement, Stripe redirige vers :
 *   /billing/success?session_id=cs_test_xxx  → afficher confirmation
 *   /billing/cancel                          → afficher message + retour
 *
 * Le webhook Stripe a déjà mis à jour profile.tier en DB côté Lambda. Ici on
 * rafraîchit juste le user en mémoire pour que l'UI affiche le nouveau tier.
 *
 * À appeler une fois au niveau RootNavigator, comme useWebOAuthCallback.
 */

import { useEffect } from 'react';
import { Platform, DeviceEventEmitter } from 'react-native';
import { useAuth } from '../context/AuthContext';
import { showAppAlert } from '../components/AppDialog';
import { showStripePopup } from '../components/StripePaymentPopup';
import { useI18n } from '../i18n';
import { ADDONS } from '../config/addonsConfig';
import { AWS_CONFIG } from '../config/aws_config';
import * as SecureStore from '../utils/secureStore';

export function useStripeCallback() {
  const { reloadProfile } = useAuth() || {};
  const { t, lang } = useI18n();

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    const path = window.location.pathname || '';
    const isFR = !lang || lang.startsWith('fr');

    // v1.12.644 — NOUVELLE APPROCHE en 2 étapes:
    // ÉTAPE A : /billing/* → set flag pending dans sessionStorage + hard
    //   reload vers '/' (écrase l'historique Stripe, plus de back possible)
    // ÉTAPE B : au reload sur '/', détecter le flag pending → afficher popup
    //   sur '/' (URL propre, AppDialog cleanup history.back va vers entrée
    //   '/' précédente, pas Stripe car écrasé par replace).
    //
    // Avantages :
    // - Le bfcache Chrome ne peut pas restaurer Stripe (replace écrase)
    // - La popup s'affiche sur '/' avec un historique propre
    // - Plus de race condition entre setTimeout et history.back

    // v1.12.648 — Helper: bloquer toute auto-paywall pendant 30s après Stripe
    // (sinon UpgradeModal s'ouvre auto au retour Home → user click "Premium
    // monthly" pensant que c'est notre popup → startCheckout en boucle)
    const setBlockPaywall = () => {
      try {
        // v1.12.654 — STICKY (no TTL): bloque les AUTO-triggers de paywall
        // pour toute la session post-Stripe. Les events ai_limit_reached,
        // wardrobe_limit_reached, guardCheck(auto=true) sont skipped.
        // Les ouvertures MANUELLES via Profile/Boutique restent fonctionnelles.
        // Clear: au refresh complet (sessionStorage scoped au tab) OU sur
        // subscribe success (reloadProfile détecte nouveau tier → clear flag).
        window.sessionStorage?.setItem('__stripe_session_block_auto__', '1');
      } catch {}
    };

    // ÉTAPE A : redirect immédiat vers '/' avec flag
    if (path.startsWith('/billing/success')) {
      console.log('[Stripe] 🎯 /billing/success → flag pending + redirect');
      setBlockPaywall();
      try { window.sessionStorage?.setItem('__stripe_pending_popup__', 'success'); } catch {}
      try { window.location.replace('/'); } catch {}
      return;
    }
    if (path.startsWith('/billing/addon-success')) {
      console.log('[Stripe] 🎯 /billing/addon-success → flag pending + redirect');
      setBlockPaywall();
      try { window.sessionStorage?.setItem('__stripe_pending_popup__', 'addon-success'); } catch {}
      try { window.location.replace('/'); } catch {}
      return;
    }
    if (path.startsWith('/billing/cancel')) {
      console.log('[Stripe] 🎯 /billing/cancel → flag pending + redirect');
      setBlockPaywall();
      try { window.sessionStorage?.setItem('__stripe_pending_popup__', 'cancel'); } catch {}
      try { window.location.replace('/'); } catch {}
      return;
    }

    // ÉTAPE B : au reload sur '/', vérifier flag pending
    let pendingPopup = null;
    try { pendingPopup = window.sessionStorage?.getItem('__stripe_pending_popup__'); } catch {}
    if (!pendingPopup) return;
    // Clear flag immédiat (sinon ré-affichage à chaque re-mount App)
    try { window.sessionStorage?.removeItem('__stripe_pending_popup__'); } catch {}

    // v1.12.650 — Force close paywalls (defensive)
    console.log('[Stripe] 🧹 force closing all paywalls (3x sur 2s)');
    DeviceEventEmitter.emit('__stripe_force_close_paywalls__');
    setTimeout(() => DeviceEventEmitter.emit('__stripe_force_close_paywalls__'), 500);
    setTimeout(() => DeviceEventEmitter.emit('__stripe_force_close_paywalls__'), 1500);

    // v1.12.653 — Le popstate guard de v1.12.652 cassait la nav (impossible
    // de revenir sur autres menus). Retiré.
    // À la place: étendre la durée du block paywall render (v1.12.651) à 5min
    // (vs 30s) pour couvrir le scénario où l'user attend longtemps avant de
    // cliquer "Accueil". Le block render est ciblé sur UpgradeModal/PremiumModal
    // donc n'affecte PAS la navigation ni les autres écrans.

    if (pendingPopup === 'success') {
      console.log('[Stripe] 📢 pending=success → popup');
      // v1.12.682 — GA4 : paiement abonnement réussi
      // v1.12.876 — Enriched : transaction_id (session_id) + value/currency
      try {
        const { trackEvent } = require('../services/AnalyticsService');
        const url = new URL(window.location.href);
        const sessionId = url.searchParams.get('session_id') || '';
        trackEvent('purchase', {
          gateway: 'stripe', kind: 'subscription', platform: 'web',
          transaction_id: sessionId.slice(0, 100),
          currency: 'EUR',
        });
      } catch {}
      // v1.12.657 — NE PAS clear le flag sticky même sur success.
      // Logs user montrent: après success, click qq part → history.back via
      // React Nav → bfcache restore URL Stripe → re-redirect.
      // Le block doit rester actif pour TOUTE la session, success OU cancel.
      // L'historique browser garde l'URL Stripe tant que le tab vit, donc
      // le block doit aussi vivre tant que le tab vit (sessionStorage scope).
      // Retry reload profile (webhook Stripe Lambda met 2-3s)
      let attempts = 0;
      const maxAttempts = 4;
      const delays = [1000, 3000, 6000, 10000];
      const tryReload = async () => {
        attempts++;
        console.log(`[Stripe] 🔄 reload attempt ${attempts}/${maxAttempts}`);
        try {
          if (typeof reloadProfile === 'function') await reloadProfile();
        } catch (e) {
          console.warn('[Stripe] reloadProfile failed:', e?.message);
        }
        if (attempts < maxAttempts) {
          setTimeout(tryReload, delays[attempts] - delays[attempts - 1]);
        }
      };
      // Lance le retry chain
      setTimeout(tryReload, delays[0]);
      // v1.12.646 — Popup brand Mytenue dédiée Stripe (Portal zIndex max,
      // pas de pushState/history.back, pas de TouchableOpacity backdrop)
      setTimeout(() => {
        try {
          showStripePopup(t('payment_success_title'), t('payment_success_body'));
        } catch (e) {
          console.warn('[Stripe] showStripePopup failed:', e?.message);
        }
      }, 500);
      return;
    }

    if (pendingPopup === 'addon-success') {
      console.log('[Stripe] 📢 pending=addon-success → popup');
      const addonId = (() => {
        try { return window.localStorage?.getItem('pending_addon_purchase'); } catch { return null; }
      })();
      // v1.12.682 — GA4 : paiement add-on réussi
      // v1.12.876 — Enriched : transaction_id + value + items GA4 standard
      try {
        const { trackEvent } = require('../services/AnalyticsService');
        const url = new URL(window.location.href);
        const sessionId = url.searchParams.get('session_id') || '';
        const ADDON_PRICES = {
          addon_tryons_3: 1.99, addon_tryons_10: 4.99, addon_tryons_25: 9.99,
          addon_scans_50: 1.49, addon_scans_200: 3.99,
          addon_wardrobe_boost_50: 1.99,
          subscription_wardrobe_plus: 0.99, subscription_wardrobe_max: 2.49,
        };
        const value = ADDON_PRICES[addonId] || 0;
        trackEvent('purchase', {
          gateway: 'stripe', kind: 'addon', addon_id: addonId || 'unknown', platform: 'web',
          transaction_id: sessionId.slice(0, 100),
          value, currency: 'EUR',
          items: [{ item_id: addonId || 'unknown', item_name: addonId || 'unknown', price: value, quantity: 1 }],
        });
      } catch {}
      try { window.localStorage?.removeItem('pending_addon_purchase'); } catch {}
      const addon = addonId ? ADDONS[addonId] : null;
      // Attendre 2s pour le webhook + fetch balance + afficher popup
      setTimeout(async () => {
        let newTotal = null;
        try {
          const token = await SecureStore.getItemAsync('outfit_access');
          if (token) {
            const r = await fetch(`${AWS_CONFIG.apiUrl}/addons/balance`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (r.ok) {
              const balance = await r.json();
              if (addon) newTotal = balance[addon.type];
            }
          }
        } catch {}
        let title, body;
        if (addon) {
          const unit = addon.type === 'tryons' ? (isFR ? 'Try-Ons' : 'Try-Ons')
                    : addon.type === 'scans'  ? (isFR ? 'analyses IA' : 'AI scans')
                    : (isFR ? 'slots dressing' : 'wardrobe slots');
          title = isFR ? '✅ Achat confirmé' : '✅ Purchase confirmed';
          if (newTotal !== null) {
            body = isFR
              ? `Votre achat de +${addon.quantity} ${unit} est validé.\n\nVous disposez maintenant de ${newTotal} ${unit} ${addon.type === 'wardrobe' ? 'bonus' : 'disponibles'}.`
              : `Your purchase of +${addon.quantity} ${unit} is confirmed.\n\nYou now have ${newTotal} ${addon.type === 'wardrobe' ? 'bonus ' : ''}${unit} available.`;
          } else {
            body = isFR
              ? `Votre achat de +${addon.quantity} ${unit} est validé. Le solde sera mis à jour dans quelques instants.`
              : `Your purchase of +${addon.quantity} ${unit} is confirmed. Balance will update in a moment.`;
          }
        } else {
          title = isFR ? '✅ Achat confirmé' : '✅ Purchase confirmed';
          body = isFR
            ? 'Votre achat a été enregistré. Le solde sera mis à jour sous peu.'
            : 'Your purchase has been recorded. Balance will update shortly.';
        }
        // v1.12.646 — Popup brand Mytenue
        try { showStripePopup(title, body); } catch {}
      }, 2000);
      return;
    }

    if (pendingPopup === 'cancel') {
      console.log('[Stripe] 📢 pending=cancel → popup');
      // v1.12.646 — Popup brand Mytenue
      setTimeout(() => {
        try { showStripePopup(t('payment_cancelled_title'), t('payment_cancelled_body')); } catch {}
      }, 500);
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export default useStripeCallback;
